import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as FiIcons from "react-icons/fi";
import { format } from "date-fns";
import SafeIcon from "../common/SafeIcon";

import { th } from "date-fns/locale"; // <-- เพิ่มบรรทัดนี้เข้าไป
const {
  FiMenu,
  FiX,
  FiTrash2,
  FiClock,
  FiSearch,
  FiChevronRight,
  FiLink2,
  FiCalendar,
  FiUsers,
  FiFileText,
  FiMapPin, // <-- เพิ่ม FiMapPinFiPaperclip
  FiPaperclip,
  FiPlay,
  FiPause,
  FiSkipBack,
  FiSkipForward,
  FiVolume2,
  FiHeadphones,
} = FiIcons;

const formatMeetingTime = (start, end) => {
  if (!start && !end) return null;
  if (start && !end) return start;
  if (!start && end) return end;
  return `${start} - ${end}`;
};

// Audio Player Component for Saved Meetings
const AudioPreview = ({ audioChunks, meetingId }) => {
  const [currentChunk, setCurrentChunk] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const audioRef = useRef(null);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const skipToPrevious = () => {
    if (currentChunk > 0) {
      setCurrentChunk(currentChunk - 1);
      setCurrentTime(0);
      setIsPlaying(false);
    }
  };

  const skipToNext = () => {
    if (currentChunk < audioChunks.length - 1) {
      setCurrentChunk(currentChunk + 1);
      setCurrentTime(0);
      setIsPlaying(false);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    if (audioRef.current) {
      const rect = e.currentTarget.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      const newTime = percent * duration;
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  useEffect(() => {
    const playableChunks = audioChunks.filter(chunk => chunk.url);
    if (playableChunks.length > 0 && audioRef.current && playableChunks[currentChunk]) {
      audioRef.current.src = playableChunks[currentChunk].url;
      audioRef.current.load();
      setCurrentTime(0);
      setDuration(0);
    }
  }, [currentChunk, audioChunks]);

  if (!audioChunks || audioChunks.length === 0) return null;

  // Filter chunks that have audio data available
  const playableChunks = audioChunks.filter(chunk => chunk.url);
  
  if (playableChunks.length === 0) {
    return (
      <div className="mt-3 p-3 bg-gray-50 rounded-lg border">
        <h4 className="text-xs font-semibold text-gray-800 mb-2 flex items-center gap-2">
          <FiHeadphones className="w-3 h-3" />
          ไฟล์ที่แบ่ง ({audioChunks.length} ไฟล์)
        </h4>
        <p className="text-xs text-gray-600">
          ไฟล์มีขนาดใหญ่เกินไป ไม่สามารถแสดงตัวอย่างเสียงได้
        </p>
      </div>
    );
  }

  return (
    <div className="mt-3 p-3 bg-gray-50 rounded-lg border">
      <h4 className="text-xs font-semibold text-gray-800 mb-2 flex items-center gap-2">
        <FiHeadphones className="w-3 h-3" />
        ตัวอย่างไฟล์ที่แบ่ง ({playableChunks.length}/{audioChunks.length} ไฟล์)
      </h4>

      {/* Chunk selector */}
      <div className="mb-2">
        <div className="flex flex-wrap gap-1">
          {audioChunks.map((chunk, index) => (
            <button
              key={index}
              onClick={() => setCurrentChunk(index)}
              className={`px-1.5 py-0.5 rounded text-xs font-medium transition-colors ${
                currentChunk === index
                  ? "bg-green-600 text-white"
                  : "bg-white text-gray-700 hover:bg-green-100 border border-gray-300"
              }`}
            >
              {index + 1}
            </button>
          ))}
        </div>
      </div>

      {/* Audio element */}
      <audio
        ref={audioRef}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={() => setIsPlaying(false)}
        className="hidden"
      />

      {/* Player controls */}
      <div className="bg-white rounded-md p-2 shadow-sm">
        {/* Progress bar */}
        <div className="mb-2">
          <div
            className="w-full h-1 bg-gray-200 rounded-full cursor-pointer"
            onClick={handleSeek}
          >
            <div
              className="h-1 bg-green-600 rounded-full transition-all duration-100"
              style={{
                width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%`,
              }}
            />
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>

        {/* Control buttons */}
        <div className="flex items-center justify-center space-x-2">
          <button
            onClick={skipToPrevious}
            disabled={currentChunk === 0}
            className="p-1 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <FiSkipBack className="w-3 h-3 text-gray-700" />
          </button>

          <button
            onClick={togglePlay}
            className="p-1.5 bg-green-600 text-white rounded-full hover:bg-green-700 transition-colors"
          >
            {isPlaying ? (
              <FiPause className="w-3 h-3" />
            ) : (
              <FiPlay className="w-3 h-3 ml-0.5" />
            )}
          </button>

          <button
            onClick={skipToNext}
            disabled={currentChunk === audioChunks.length - 1}
            className="p-1 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <FiSkipForward className="w-3 h-3 text-gray-700" />
          </button>

          <div className="flex items-center space-x-1 ml-2">
            <FiVolume2 className="w-3 h-3 text-gray-600" />
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={volume}
              onChange={handleVolumeChange}
              className="w-12 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const HistoryMenu = ({ history = [], onSelectHistory, onDelete, getAudioChunksForSavedMeeting }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  const handleSelect = (meetingId) => {
    setSelectedId(meetingId);
    if (onSelectHistory) onSelectHistory(meetingId);
    setIsOpen(false);
  };

  const handleDelete = (id, title, e) => {
    e.stopPropagation();
    if (onDelete) onDelete(id, title);
  };

  const filteredHistory = history.filter((item) => {
    if (!searchTerm.trim()) return true;
    const searchLower = searchTerm.toLowerCase();
    const details = item.meetingDetails || {};
    return (
      (details.meetingTitle || item.title)
        ?.toLowerCase()
        .includes(searchLower) ||
      item.transcript?.content?.toLowerCase().includes(searchLower) ||
      details.agenda?.toLowerCase().includes(searchLower) ||
      details.attendees?.toLowerCase().includes(searchLower) ||
      details.location?.toLowerCase().includes(searchLower) // <-- เพิ่ม location ในการค้นหา
    );
  });

  const panelVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.07, delayChildren: 0.2 },
    },
  };

  const cardVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100 },
    },
    exit: { opacity: 0, scale: 0.95, transition: { duration: 0.2 } },
  };

  return (
    <>
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="p-3 rounded-full shadow-md transition-colors bg-white text-green-700 hover:bg-green-700 hover:text-white"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        aria-label="Toggle history menu"
      >
        <SafeIcon icon={isOpen ? FiX : FiMenu} className="w-5 h-5" />
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
              onClick={() => setIsOpen(false)}
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 400, damping: 40 }}
              className="fixed right-0 top-0 z-50 h-full w-full max-w-lg bg-slate-100 shadow-2xl flex flex-col"
            >
              <div className="p-4 border-b border-slate-200 flex-shrink-0">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-green-700">
                    ประวัติรายการทั้งหมด
                  </h2>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-1.5 text-slate-500 hover:text-green-700 hover:bg-green-100 rounded-full"
                  >
                    <SafeIcon icon={FiX} className="w-6 h-6" />
                  </button>
                </div>
                <div className="relative">
                  <SafeIcon
                    icon={FiSearch}
                    className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5"
                  />
                  <input
                    type="text"
                    placeholder="ค้นหา..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-11 pr-4 py-2.5 bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-700"
                  />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-3">
                <motion.div
                  variants={panelVariants}
                  initial="hidden"
                  animate="visible"
                >
                  <AnimatePresence>
                    {filteredHistory.length > 0 ? (
                      filteredHistory.map((item) => {
                        const details = item.meetingDetails || {};
                        const meetingDate = details.date || details.meetingDate;
                        const meetingTime = formatMeetingTime(
                          details.startTime,
                          details.endTime
                        );
                        const attachments = item.attachments || [];
                        const attendees =
                          details.attendees || details.participants;

                        return (
                          <motion.div
                            layout="position"
                            key={item.id}
                            variants={cardVariants}
                            exit="exit"
                            onClick={() => handleSelect(item.id)}
                            className={`group p-4 mb-2.5 bg-white rounded-xl shadow-sm cursor-pointer border-2 transition-all duration-300 ${
                              selectedId === item.id
                                ? "border-green-700 bg-green-50/50 shadow-md"
                                : "border-transparent hover:border-green-400 hover:shadow-lg hover:-translate-y-1"
                            }`}
                          >
                            <div className="flex justify-between items-start gap-3">
                              <h3 className="font-semibold text-slate-800 text-base group-hover:text-green-700 transition-colors">
                                {details.meetingTitle ||
                                  details.title ||
                                  item.title}
                              </h3>
                              <div className="flex items-center flex-shrink-0 -mt-1">
                                <button
                                  onClick={(e) =>
                                    handleDelete(item.id, item.title, e)
                                  }
                                  className="p-1.5 text-slate-400 rounded-full transition-all opacity-0 group-hover:opacity-100 hover:text-red-600 hover:bg-red-100"
                                  title="Delete"
                                >
                                  <SafeIcon
                                    icon={FiTrash2}
                                    className="w-4 h-4"
                                  />
                                </button>
                                <FiChevronRight className="w-5 h-5 text-slate-300 transition-transform group-hover:translate-x-0.5" />
                              </div>
                            </div>

                            <p className="mt-2 text-sm text-slate-600 line-clamp-2">
                              {item.transcript?.content ||
                                "No transcript content"}
                            </p>

                            {/* --- CORRECTED & RE-ARRANGED DETAILS SECTION --- */}
                            {(meetingDate ||
                              meetingTime ||
                              details.location ||
                              attendees) && (
                              <div className="mt-3.5 flex flex-col gap-2.5 border-t border-slate-200/60 pt-3 text-sm text-slate-600">
                                {/* Row 1: Date & Time */}
                                {(meetingDate || meetingTime) && (
                                  <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
                                    {meetingDate && (
                                      <div className="flex items-center gap-2">
                                        <FiCalendar className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                        <span>
                                          {/* 
        - new Date(meetingDate) แปลง string เป็น object วันที่
        - format แปลง object วันที่เป็น string ในรูปแบบที่ต้องการ
        - 'd MMMM yyyy' คือรูปแบบ "วัน เดือน(ชื่อเต็ม) ปี"
        - { locale: th } คือการบอกให้ใช้เดือนเป็นภาษาไทย
      */}
                                          {meetingDate && !isNaN(new Date(meetingDate).getTime()) ? format(
                                            new Date(meetingDate),
                                            "d MMMM yyyy",
                                            { locale: th }
                                          ) : null}
                                        </span>
                                      </div>
                                    )}
                                    {meetingTime && (
                                      <div className="flex items-center gap-2">
                                        <FiClock className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                        <span>{meetingTime}</span>
                                      </div>
                                    )}
                                  </div>
                                )}

                                <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
                                  {/* Row 2: Location */}
                                  {details.location && (
                                    <div className="flex items-start gap-2">
                                      <FiMapPin className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                                      <span>{details.location}</span>
                                    </div>
                                  )}

                                  {/* Row 3: Attendees */}
                                  {attendees && (
                                    <div className="flex items-start gap-2">
                                      <FiUsers className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                                      <span className="line-clamp-2">
                                        {attendees}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {details.agenda && (
                              <div className="mt-3 text-sm p-3 bg-slate-50 rounded-md border border-slate-200/60">
                                <div className="flex items-center gap-2 font-medium text-slate-700">
                                  <FiFileText className="w-4 h-4 text-slate-500" />
                                  <span>หัวข้อการประชุม</span>
                                </div>
                                <p className="mt-1 text-slate-600 line-clamp-2">
                                  {details.agenda}
                                </p>
                              </div>
                            )}

                            <div className="mt-4 pt-3 border-t border-slate-200/80 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 text-xs">
                              <div className="text-slate-500">
                                <span className="font-medium">
                                  วันที่ถอดความ:
                                </span>{" "}
                                {item.startTime ? format(new Date(item.startTime), "dd/MM/yy") : '-'}
                              </div>
                              <div className="text-slate-500">
                                <span className="font-medium">ระยะเวลา:</span>{" "}
                                {item.transcript?.metadata?.duration || "-"}
                              </div>
                              <div className="text-slate-500">
                                <span className="font-medium">ขนาดไฟล์:</span>{" "}
                                {item.transcript?.metadata?.fileSize || "-"}
                              </div>
                              {item.referencedMeetingIds?.length > 0 && (
                                <div className="col-span-full sm:col-span-1 flex items-center gap-1 text-green-700 font-medium">
                                  <FiLink2 className="w-3.5 h-3.5" />
                                  <span>
                                    {item.referencedMeetingIds.length} อ้างอิง
                                  </span>
                                </div>
                              )}
                              {attachments.length > 0 && (
                                <div className="flex items-center gap-1 text-sky-600 font-medium">
                                  <FiPaperclip className="w-3.5 h-3.5" />
                                  <span>{attachments.length} ไฟล์แนบ</span>
                                </div>
                              )}
                            </div>

                            {/* Audio Preview - Disabled for saved meetings (session-only audio) */}
                            {/* Audio chunks are only available during the current session */}
                          </motion.div>
                        );
                      })
                    ) : (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="flex flex-col items-center justify-center h-full p-4 text-center text-slate-500"
                      >
                        <FiSearch className="w-16 h-16 mb-4 text-green-200" />
                        <p className="text-lg font-semibold text-slate-700">
                          ไม่พบผลลัพธ์
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default HistoryMenu;
